import { PDFDocument } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
).toString();

const PDF_HEADER = '%PDF-';

function decodeLatin1(bytes) {
    return new TextDecoder('latin1').decode(bytes);
}

function bytesEqual(a, b) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
        if (a[i] !== b[i]) return false;
    }
    return true;
}

function isPasswordError(err) {
    const msg = String(err?.message || '').toLowerCase();
    return msg.includes('password') || msg.includes('encrypt');
}

/**
 * Fix common structural issues: leading garbage before %PDF- and a missing %%EOF trailer.
 */
function normalizePdfBytes(bytes) {
    if (!bytes?.length) return bytes;

    let start = 0;
    const head = decodeLatin1(bytes.slice(0, Math.min(bytes.length, 65536)));
    const headerIdx = head.indexOf(PDF_HEADER);
    if (headerIdx > 0) start = headerIdx;

    let normalized = bytes.slice(start);
    const tail = decodeLatin1(normalized.slice(Math.max(0, normalized.length - 4096)));
    if (!/%EOF\b/.test(tail)) {
        const eof = new TextEncoder().encode('\n%%EOF\n');
        const merged = new Uint8Array(normalized.length + eof.length);
        merged.set(normalized);
        merged.set(eof, normalized.length);
        normalized = merged;
    }

    return normalized;
}

/**
 * Encrypted PDFs must be refused up front: pdf-lib is loaded with ignoreEncryption, so it would
 * happily "repair" one into a file whose streams are still ciphertext and can't be opened.
 * The trailer's /Encrypt entry sits in the last chunk (or the first, for linearized files).
 */
function looksEncrypted(bytes) {
    const CHUNK = 65536;
    const hasEncrypt = (slice) => /\/Encrypt\s+\d+\s+\d+\s+R|\/Encrypt\s*<</.test(decodeLatin1(slice));
    return hasEncrypt(bytes.subarray(Math.max(0, bytes.length - CHUNK)))
        || hasEncrypt(bytes.subarray(0, Math.min(bytes.length, CHUNK)));
}

function buildRepairInputs(bytes) {
    const inputs = [bytes];
    const normalized = normalizePdfBytes(bytes);
    if (!bytesEqual(bytes, normalized)) inputs.push(normalized);
    return inputs;
}

async function loadWithPdfJs(bytes) {
    return pdfjsLib.getDocument({
        data: bytes,
        stopAtErrors: false,
    }).promise;
}

async function outputHasPages(bytes) {
    try {
        const pdf = await PDFDocument.load(bytes, {
            ignoreEncryption: true,
            throwOnInvalidObject: false,
            updateMetadata: false,
        });
        if (pdf.getPageCount() > 0) return true;
    } catch {
        // fall through to pdf.js
    }

    try {
        const pdf = await loadWithPdfJs(bytes);
        return pdf.numPages > 0;
    } catch {
        return false;
    }
}

async function repairTierRewrite(bytes) {
    const pdf = await PDFDocument.load(bytes, {
        ignoreEncryption: true,
        throwOnInvalidObject: false,
        updateMetadata: false,
    });
    if (!pdf.getPageCount()) throw new Error('empty');
    const output = await pdf.save({ useObjectStreams: false });
    if (!(await outputHasPages(output))) throw new Error('invalid output');
    return output;
}

async function repairTierRebuild(bytes) {
    const source = await PDFDocument.load(bytes, {
        ignoreEncryption: true,
        throwOnInvalidObject: false,
        updateMetadata: false,
    });
    const indices = source.getPageIndices();
    if (!indices.length) throw new Error('empty');

    const rebuilt = await PDFDocument.create();
    const pages = await rebuilt.copyPages(source, indices);
    for (const page of pages) rebuilt.addPage(page);

    const output = await rebuilt.save({ useObjectStreams: false });
    if (!(await outputHasPages(output))) throw new Error('invalid output');
    return output;
}

async function canvasToJpegBytes(canvas, quality = 0.92) {
    const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('render failed'))), 'image/jpeg', quality);
    });
    return new Uint8Array(await blob.arrayBuffer());
}

async function repairTierRender(bytes, onPage) {
    const pdfJs = await loadWithPdfJs(bytes);

    const total = pdfJs.numPages;
    if (!total) throw new Error('empty');

    const out = await PDFDocument.create();
    const scale = 2;
    let renderedPages = 0;

    for (let i = 1; i <= total; i += 1) {
        onPage?.(i, total);
        try {
            const page = await pdfJs.getPage(i);
            const viewport = page.getViewport({ scale });
            const canvas = document.createElement('canvas');
            canvas.width = Math.max(1, Math.floor(viewport.width));
            canvas.height = Math.max(1, Math.floor(viewport.height));
            const ctx = canvas.getContext('2d', { alpha: false });
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            await page.render({ canvasContext: ctx, viewport, background: '#ffffff' }).promise;

            const jpegBytes = await canvasToJpegBytes(canvas);
            const image = await out.embedJpg(jpegBytes);
            const pageW = viewport.width / scale;
            const pageH = viewport.height / scale;
            const newPage = out.addPage([pageW, pageH]);
            newPage.drawImage(image, { x: 0, y: 0, width: pageW, height: pageH });
            renderedPages += 1;
        } catch {
            // Skip pages that cannot be rendered; recover whatever we can.
        }
    }

    if (!renderedPages) throw new Error('empty');

    const output = await out.save({ useObjectStreams: false });
    if (!(await outputHasPages(output))) throw new Error('invalid output');
    return output;
}

async function attemptRepairTiers(bytes, callbacks) {
    const errors = [];

    try {
        callbacks?.onTier?.(1);
        const output = await repairTierRewrite(bytes);
        return { blob: new Blob([output], { type: 'application/pdf' }), tier: 1 };
    } catch (err) {
        if (isPasswordError(err)) throw new Error('password');
        errors.push(err);
    }

    try {
        callbacks?.onTier?.(2);
        const output = await repairTierRebuild(bytes);
        return { blob: new Blob([output], { type: 'application/pdf' }), tier: 2 };
    } catch (err) {
        if (isPasswordError(err)) throw new Error('password');
        errors.push(err);
    }

    try {
        callbacks?.onTier?.(3);
        const output = await repairTierRender(bytes, callbacks?.onPage);
        return { blob: new Blob([output], { type: 'application/pdf' }), tier: 3 };
    } catch (err) {
        if (isPasswordError(err)) throw new Error('password');
        errors.push(err);
    }

    throw errors[errors.length - 1] || new Error('failed');
}

/**
 * Attempt to repair a damaged PDF using three progressively stronger methods.
 * @param {File} file
 * @param {{ onTier?: (tier: number) => void, onPage?: (page: number, total: number) => void }} [callbacks]
 * @returns {Promise<{ blob: Blob, tier: number }>}
 */
export async function repairPdf(file, callbacks) {
    const bytes = new Uint8Array(await file.arrayBuffer());
    if (looksEncrypted(bytes)) throw new Error('password');
    const inputs = buildRepairInputs(bytes);
    const errors = [];

    for (const input of inputs) {
        try {
            return await attemptRepairTiers(input, callbacks);
        } catch (err) {
            if (err?.message === 'password') throw err;
            errors.push(err);
        }
    }

    throw new Error('failed');
}
